import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  ACTIVE_DRAFT_EVENT,
  clearActiveDraftOrderId,
  getActiveDraftOrderId,
} from '../lib/activeDraftOrder';
import { log } from '../lib/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Reactive view of the customer's in-progress draft cart.
 *
 * Reads sessionStorage('eg3_active_draft_order_id') and, when populated,
 * fetches the order so the TopNav badge can show "N items · $XX.XX".
 *
 * Re-syncs on:
 *   - the lib-dispatched 'eg3:active-draft-changed' custom event
 *     (set / clear / notifyActiveDraftChanged)
 *   - the native 'storage' event (sister tabs flipping the value)
 *   - the `routeKey` arg changing (caller passes location.pathname so a
 *     navigate() to /review-order auto-refreshes the count)
 *
 * Self-heals on 404 / 401 by clearing the stale id so the badge
 * disappears instead of getting stuck on a phantom count.
 */
export const useActiveDraftCart = ({ routeKey, isAuthenticated }) => {
  const [orderId, setOrderId] = useState(getActiveDraftOrderId);
  const [itemCount, setItemCount] = useState(0);
  const [totalPrice, setTotalPrice] = useState(0);
  const [currency, setCurrency] = useState('CAD');
  // Tick increments on every change-event fire — even when the order id
  // didn't change (e.g. add-item to same draft) — so the refetch effect
  // below re-runs and pulls the new item count. Without this, React's
  // Object.is bail-out on setOrderId(sameValue) would skip the refetch.
  const [tick, setTick] = useState(0);
  const fetchAbort = useRef(null);

  // Re-read sessionStorage whenever the lib fires its custom event,
  // another tab writes to it, or the route changes.
  useEffect(() => {
    const sync = () => {
      setOrderId(getActiveDraftOrderId());
      setTick((n) => n + 1);
    };
    window.addEventListener(ACTIVE_DRAFT_EVENT, sync);
    window.addEventListener('storage', sync);
    sync();
    return () => {
      window.removeEventListener(ACTIVE_DRAFT_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, [routeKey]);

  const refetch = useCallback(async () => {
    const id = getActiveDraftOrderId();
    if (!id || !isAuthenticated) {
      setItemCount(0);
      setTotalPrice(0);
      return;
    }
    if (fetchAbort.current) fetchAbort.current.abort();
    const ctrl = new AbortController();
    fetchAbort.current = ctrl;
    try {
      const { data } = await axios.get(
        `${API_URL}/api/enhanced-orders/${id}`,
        { signal: ctrl.signal },
      );
      const status = (data?.status || '').toLowerCase();
      // Cart is the IN-PROGRESS draft only — past orders shouldn't show
      // a "View cart" CTA. The companion sync in ReviewOrderPage /
      // OrderSummaryPage already clears sessionStorage on non-draft
      // status, but defend in depth here too.
      if (status && status !== 'draft') {
        clearActiveDraftOrderId();
        setItemCount(0);
        setTotalPrice(0);
        return;
      }
      setItemCount((data?.items || []).length);
      setTotalPrice(Number(data?.total_price) || 0);
      setCurrency((data?.currency || 'CAD').toUpperCase());
    } catch (err) {
      if (axios.isCancel(err) || err.name === 'CanceledError') return;
      const status = err.response?.status;
      // 404 = stale id; 401 = session lost. Both → drop the pointer so
      // the badge disappears cleanly.
      if (status === 404 || status === 401) {
        clearActiveDraftOrderId();
      } else if (process.env.NODE_ENV === 'development') {
        log.warn('useActiveDraftCart fetch failed:', err);
      }
      setItemCount(0);
      setTotalPrice(0);
    }
  }, [isAuthenticated]);

  // Refetch whenever orderId / route / auth / tick changes. The tick
  // dep is what lets us re-pull when a same-id event fires (add/remove
  // item on the SAME draft), since orderId itself didn't actually
  // change in that case.
  useEffect(() => {
    refetch();
    return () => {
      if (fetchAbort.current) fetchAbort.current.abort();
    };
  }, [orderId, routeKey, isAuthenticated, tick, refetch]);

  return { orderId, itemCount, totalPrice, currency, refetch };
};

export default useActiveDraftCart;
